import React, { useEffect, useRef, useState } from "react";
import {
  ChevronRight,
  Send,
  ShieldCheck,
  Upload,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "../api/api";
import toast from "react-hot-toast";
import gsap from "gsap";

const HERO_IMAGE = "/assets/images/herobg.jpeg";

const ComplaintPage = () => {
  const pageRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [tracking, setTracking] = useState("");

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    school_affected: "",
    lga: "",
    complaint_type: "",
    message: "",
    evidence_url: "",
    honeypot: "",
  });

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", { opacity: 0, y: 30, duration: 0.8 });
      gsap.from(".hero-title", { opacity: 0, y: 80, duration: 1.1 });
      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        duration: 0.9,
        delay: 0.25,
      });

      gsap.from(".complaint-form-card", {
        opacity: 0,
        y: 60,
        duration: 1,
        delay: 0.3,
      });

      gsap.to(".hero-orb-one", {
        y: 40,
        x: -25,
        repeat: -1,
        yoyo: true,
        duration: 6,
        ease: "sine.inOut",
      });

      gsap.to(".hero-orb-two", {
        y: -35,
        x: 25,
        repeat: -1,
        yoyo: true,
        duration: 7,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  const uploadEvidence = async (file) => {
    if (!file) return;

    try {
      setUploading(true);

      const body = new FormData();
      body.append("file", file);

      const res = await api.post("/media/upload", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setForm((prev) => ({ ...prev, evidence_url: res.data.url }));
      toast.success("Evidence uploaded");
    } catch (err) {
      toast.error(err.cleanMessage || "Evidence upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();

    try {
      const res = await api.post("/complaints", form);

      setTracking(res.data.tracking_number);
      toast.success("Complaint submitted successfully");

      setForm({
        name: "",
        phone: "",
        email: "",
        school_affected: "",
        lga: "",
        complaint_type: "",
        message: "",
        evidence_url: "",
        honeypot: "",
      });
    } catch (err) {
      toast.error(err.cleanMessage || "Complaint submission failed");
    }
  };

  return (
    <main ref={pageRef} className="bg-slate-50 overflow-hidden">
      <section className="relative overflow-hidden">
        <div className="relative min-h-[640px] bg-[#04160a] pt-28 pb-28 overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt="Citizen Feedback Form"
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-[#04160a]/80" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/95 via-[#06183f]/75 to-[#ff6600]/35" />

          <div className="hero-orb-one absolute -top-24 -left-24 w-[460px] h-[460px] rounded-full bg-[#06183f]/35 blur-3xl" />
          <div className="hero-orb-two absolute -bottom-28 -right-24 w-[460px] h-[460px] rounded-full bg-[#ff6600]/30 blur-3xl" />

          <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:80px_80px]" />

          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <div className="hero-badge inline-flex items-center gap-3 mb-5">
              <span className="w-10 h-[3px] bg-[#ff6600]" />
              <span className="text-[#ff6600] font-bold tracking-[0.2em] text-sm uppercase">
                Accountability Desk
              </span>
            </div>

            <h1
              className="hero-title text-5xl md:text-7xl lg:text-[82px] font-black text-white leading-[1.02]"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Citizen Feedback Form
            </h1>

            <p className="hero-text text-white/85 max-w-3xl mx-auto mt-6 leading-relaxed text-[17px]">
              Submit complaints or feedback relating to schools, projects,
              teacher conduct, infrastructure, illegal levies and pupil welfare.
            </p>

            <div className="hero-text flex items-center justify-center gap-2 mt-6 text-sm text-white/80 flex-wrap">
              <Link to="/" className="hover:text-[#ff6600] transition-colors">
                Home
              </Link>
              <ChevronRight size={14} />
              <span className="text-[#ff6600]">Complaints</span>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-16">
        {tracking && (
          <div className="mb-8 bg-green-50 border border-green-200 rounded-3xl p-6">
            <h3 className="font-black text-green-700 text-xl">
              Complaint Submitted
            </h3>
            <p className="text-green-700 mt-2">Your tracking number is:</p>
            <p className="text-2xl font-black text-[#06183f] mt-2">
              {tracking}
            </p>
          </div>
        )}

        <form
          onSubmit={submit}
          className="complaint-form-card bg-white rounded-[34px] p-6 md:p-8 shadow-[0_18px_45px_rgba(0,0,0,0.12)] border border-slate-100"
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-14 h-14 rounded-2xl bg-[#06183f]/10 text-[#06183f] flex items-center justify-center shrink-0">
              <ShieldCheck size={26} />
            </div>

            <div>
              <h2
                className="text-2xl md:text-3xl font-black text-[#06183f]"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Complaint and Feedback Data Bank
              </h2>
              <p className="text-slate-500 text-sm">
                Your individual complaint will not be displayed publicly.
              </p>
            </div>
          </div>

          <input
            type="text"
            value={form.honeypot}
            onChange={(e) => setForm({ ...form, honeypot: e.target.value })}
            className="hidden"
            tabIndex="-1"
            autoComplete="off"
          />

          <div className="grid md:grid-cols-2 gap-4">
            <input
              required
              placeholder="Name of Complainant"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]"
            />

            <input
              placeholder="Phone Number"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]"
            />

            <input
              type="email"
              placeholder="Email Address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]"
            />

            <input
              required
              placeholder="School Affected"
              value={form.school_affected}
              onChange={(e) =>
                setForm({ ...form, school_affected: e.target.value })
              }
              className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]"
            />

            <input
              required
              placeholder="LGA"
              value={form.lga}
              onChange={(e) => setForm({ ...form, lga: e.target.value })}
              className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]"
            />

            <select
              required
              value={form.complaint_type}
              onChange={(e) =>
                setForm({ ...form, complaint_type: e.target.value })
              }
              className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f] bg-white"
            >
              <option value="">Select Type of Complaint</option>
              <option>Project complaint</option>
              <option>Teacher conduct complaint</option>
              <option>Infrastructure complaint</option>
              <option>Illegal levy complaint</option>
              <option>Pupil welfare complaint</option>
              <option>General feedback</option>
            </select>
          </div>

          <textarea
            required
            rows="7"
            placeholder="Describe the complaint or feedback"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="mt-4 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f] resize-none"
          />

          <div className="mt-4 border border-dashed border-slate-300 rounded-2xl p-5">
            <label className="flex items-center gap-3 font-bold text-[#06183f] cursor-pointer">
              <Upload size={20} />
              Upload Evidence
              <input
                type="file"
                accept="image/*"
                onChange={(e) => uploadEvidence(e.target.files?.[0])}
                className="hidden"
              />
            </label>

            {uploading && (
              <p className="text-sm text-slate-500 mt-2">Uploading...</p>
            )}

            {form.evidence_url && (
              <p className="text-sm text-green-600 mt-2">
                Evidence uploaded successfully.
              </p>
            )}
          </div>

          <button className="mt-6 inline-flex items-center gap-2 px-7 py-4 rounded-2xl bg-[#06183f] hover:bg-[#ff6600] text-white font-black transition-colors">
            Submit Complaint <Send size={18} />
          </button>
        </form>
      </section>
    </main>
  );
};

export default ComplaintPage;